import { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { LoadingContainer } from "../components/LoadingContainer";
import { InfoBox } from "../components/InfoBox";
import { SidebarPanel } from "../components/SidebarPanel";
import { CreditNoteForm } from "./components/CreditNoteForm";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import {
  useGetCreditNotesQuery,
  useCreateCreditNoteMutation,
  useUpdateCreditNoteMutation,
  useDeleteCreditNoteMutation,
} from "../query/creditNotesQuery";
import { useIsAdmin, useIsReseller } from "../hooks/useIsAdmin";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatDate } from "../utils/dateUtils";
import type { StoreId, CreditNoteId } from "@KassaroAB/shared/types/ids";
import type { RestCreateCreditNote, RestCreditNote } from "@KassaroAB/shared/types/creditNote";
import { formatMoney } from "../utils/formatMoney";

const DeleteButton = styled(Button)`
  background-color: ${colors.danger};
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

export const CreditNotesView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const [selectedCreditNoteId, setSelectedCreditNoteId] = useState<CreditNoteId | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isAdmin = useIsAdmin();
  const isReseller = useIsReseller();
  const canEdit = isAdmin || isReseller;

  const {
    data: creditNotes,
    isLoading: isLoadingCreditNotes,
    error: creditNotesError,
  } = useGetCreditNotesQuery(storeId ?? null);

  const { mutate: createMutation, error: createError, isPending: isCreatePending } =
    useCreateCreditNoteMutation(storeId ?? null);
  const { mutate: updateMutation, error: updateError, isPending: isUpdatePending } =
    useUpdateCreditNoteMutation(storeId ?? null);
  const { mutate: deleteMutation, error: deleteError } =
    useDeleteCreditNoteMutation(storeId ?? null);

  const selectedCreditNote = creditNotes?.find((c) => c.id === selectedCreditNoteId);

  const handleCreditNoteClick = (creditNote: RestCreditNote) => {
    if (!canEdit) return;
    setSelectedCreditNoteId(creditNote.id);
    setIsCreating(false);
    setIsPanelOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedCreditNoteId(null);
    setIsCreating(true);
    setIsPanelOpen(true);
  };

  const handleCreate = (data: RestCreateCreditNote) => {
    createMutation(data, {
      onSuccess: () => {
        setSuccessMessage(t("creditNotesView.successCreated"));
        setIsPanelOpen(false);
        setIsCreating(false);
        setSelectedCreditNoteId(null);
      },
      onError: () => {
        setSuccessMessage(null);
      },
    });
  };

  const handleUpdate = (data: RestCreateCreditNote) => {
    if (!selectedCreditNoteId) return;
    updateMutation(
      {
        creditNoteId: selectedCreditNoteId,
        creditNote: data,
      },
      {
        onSuccess: () => {
          setSuccessMessage(t("creditNotesView.successUpdated"));
          setIsPanelOpen(false);
          setSelectedCreditNoteId(null);
        },
        onError: () => {
          setSuccessMessage(null);
        },
      },
    );
  };

  const handleDelete = (creditNoteId: CreditNoteId, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t("creditNotesView.deleteConfirm"))) {
      deleteMutation(creditNoteId, {
        onSuccess: () => {
          setSuccessMessage(t("creditNotesView.successDeleted"));
        },
        onError: () => {
          setSuccessMessage(null);
        },
      });
    }
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setIsCreating(false);
    setSelectedCreditNoteId(null);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoadingCreditNotes) {
    return (
      <PageContent title={t("creditNotesView.title")}>
        <LoadingContainer message={t("creditNotesView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("creditNotesView.title")}
      actions={
        canEdit ? (
          <Button onClick={handleCreateClick}>{t("creditNotesView.create")}</Button>
        ) : undefined
      }
    >
      {creditNotesError && (
        <InfoBox variant="error">
          {t("creditNotesView.errorLoad")}
        </InfoBox>
      )}

      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">
          {t("creditNotesView.errorSave")}
        </InfoBox>
      )}

      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}

      {creditNotes && creditNotes.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("creditNotesView.tableBarcode")}</TableHeaderCell>
                <TableHeaderCell>{t("creditNotesView.tableOriginalAmount")}</TableHeaderCell>
                <TableHeaderCell>{t("creditNotesView.tableRemainingAmount")}</TableHeaderCell>
                <TableHeaderCell>{t("creditNotesView.tableUseByDate")}</TableHeaderCell>
                <TableHeaderCell>{t("creditNotesView.tableCreated")}</TableHeaderCell>
                {canEdit && <TableHeaderCell>{t("creditNotesView.tableActions")}</TableHeaderCell>}
              </tr>
            </TableHeader>
            <TableBody>
              {creditNotes.map((creditNote) => (
                <TableRow
                  key={creditNote.id}
                  clickable={canEdit}
                  onClick={() => handleCreditNoteClick(creditNote)}
                >
                  <TableCell noWrap>{creditNote.barcode}</TableCell>
                  <TableCell noWrap>
                    {formatMoney(creditNote.originalAmount)}
                  </TableCell>
                  <TableCell noWrap>
                    {formatMoney(creditNote.remainingAmount)}
                  </TableCell>
                  <TableCell noWrap>{formatDate(creditNote.useByDate)}</TableCell>
                  <TableCell noWrap>{formatDate(creditNote.createdAt)}</TableCell>
                  {canEdit && (
                    <TableCell noWrap>
                      <DeleteButton onClick={(e) => handleDelete(creditNote.id, e)}>
                        {t("creditNotesView.delete")}
                      </DeleteButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <div style={{ textAlign: "center", color: colors.textMuted }}>
            {t("creditNotesView.empty")}
            {canEdit && ` ${t("creditNotesView.emptyHint")}`}
          </div>
        </Card>
      )}

      {canEdit && (selectedCreditNote || isCreating) && (
        <SidebarPanel
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          title={
            isCreating ? t("creditNotesView.panelCreate") : t("creditNotesView.panelEdit")
          }
        >
          <CreditNoteForm
            creditNote={selectedCreditNote || undefined}
            onSubmit={selectedCreditNote ? handleUpdate : handleCreate}
            onCancel={handlePanelClose}
            isLoading={isCreatePending || isUpdatePending}
          />
        </SidebarPanel>
      )}
    </PageContent>
  );
};
